import {
  takeLatest, select, call, put,
} from 'redux-saga/effects';

import moment from 'moment-timezone';
import { getViewerSessionsScanDataApi } from '@api/viewerSessions';
import { message } from 'antd';
import {
  defaultMetricColumns,
  FIXED_REQUIRED_COLUMNS,
} from '@components/viewer-sessions/utils';
import selector from './selectors';
import * as types from './types';
import { ORIGIN_FUNCDATA } from './reducer';
import {
  SET_CURRENT_ERROR_CODE,
  SET_BASE_STATE,
} from '../errorDiagnostics/base/types';
import { SELECTED_METRIC_CHART } from '../metricsChartContainer/types';
import { metricSelector } from '../errorDiagnostics/base/selectors';
import errorDashboardSelector from '../errorDiagnostics/dashboard/selectors';
import commonSelector, { featureSelector } from '../common/selectors';
import featureNames from '../../utils/featureToggle/constants';
import { metricFilterLookupSelector } from '@store/trends/base/selectors';

function* getViewerSessionsData(action) {
  const { filter, from, to, name, metric, metricFilter } = action;

  const store = yield select(selector);
  const { exceedTimeLimit } = store[name];
  const { features } = yield select(commonSelector);

  if (exceedTimeLimit) {
    yield put({
      type: types.GET_SCAN_DATA_FAIL,
      name,
      errorCode: 500,
      errorMsg: 'Invalid interval',
    });
    return;
  }

  const timelineEnabled = yield select(featureSelector(featureNames.VIEWER_DRILLDOWN_TIMELINE));
  const customerAccountType = yield select(featureSelector(featureNames.CUSTOMER_ACCOUNT_TYPE));
  const defaultColumns = defaultMetricColumns(
    metric,
    timelineEnabled,
    customerAccountType
  );
  let { selectedMetrics } = store[name];
  if (!selectedMetrics.length) {
    selectedMetrics = defaultColumns;
  }
  if (features[featureNames.VIEWER_DRILLDOWN_TIMELINE]) {
    selectedMetrics = [...FIXED_REQUIRED_COLUMNS, ...selectedMetrics];
  }

  const metricList: any = [];
  /**
   * If metric is an object, we need to get the metric name from it. This is part of the fix for CE-8392
   * here are certaing error dashbaords for which the ViewerSession state is in broke state.
   * The selected metrics are in format of [{metrc: 'metric1', show: true}, {metric: 'metric2', show: false}]
   * The expected format is [metric1, metric2].
   * This is a patch up fix as we still don't know what steps actually broke the state.
   */
  selectedMetrics.forEach((metric) => {
    const pushMetric = typeof metric === 'object' ? metric.metric : metric;
    metricList.push(pushMetric);
  });

  try {
    const reqBody = {
      interval: `${moment(from).toISOString()}/${moment(to).toISOString()}`,
      columns: Array.from(new Set(metricList)),
      filter,
      selectedMetric: metric,
    };

    if (metricFilter && metricFilter.length > 0) {
      reqBody.metricFilter = metricFilter;
    }

    yield put({
      type: types.SAVE_SCAN_QUERY,
      name: 'query',
      body: reqBody,
    });

    const { data } = yield call(getViewerSessionsScanDataApi, reqBody);

    yield put({
      type: types.GET_SCAN_DATA_SUCCESSFUL,
      data,
      name,
    });

    yield put({
      type: types.SET_VIEWER_SESSIONS_PARAMS,
      name,
      params: {
        funcData: { ...ORIGIN_FUNCDATA },
      },
    });
  } catch (e) {
    yield call(message.error, e.message);
    yield put({
      type: types.GET_SCAN_DATA_FAIL,
      name,
      errorCode: 500,
      errorMsg: 'Invalid interval',
    });
  }
}

function* watchGetViewerSessionsData() {
  yield takeLatest([types.GET_SCAN_DATA], getViewerSessionsData);
}

function* changeDefaultColumns(action) {
  const { metricName, name = 'trends' } = action;
  const timelineEnabled = yield select(featureSelector(featureNames.VIEWER_DRILLDOWN_TIMELINE));
  const customerAccountType = yield select(featureSelector(featureNames.CUSTOMER_ACCOUNT_TYPE));
  const defaultColumns = defaultMetricColumns(metricName, timelineEnabled, customerAccountType);

  const metricFilterLookup = yield select(metricFilterLookupSelector);

  // add the primary metric columns first
  // iterate through metric filter metrics (other than primary)
  // add unique columns
  Object.keys(metricFilterLookup).forEach(metric => {
    if(metric === metricName) return;
    const colSet = new Set(defaultColumns);
    const metricsColumns = defaultMetricColumns(metric, timelineEnabled, customerAccountType);
    metricsColumns.forEach( mc => {
      if(!colSet.has(mc)) {
        defaultColumns.push(mc);
      }
    });
  });

  if (defaultColumns) {
    yield put({
      type: types.UPDATE_SELECTED_METRICS,
      newMetrics: defaultColumns,
      name,
    });
  }
}

function* watchSelectedMetricChange() {
  yield takeLatest([
    SELECTED_METRIC_CHART,
  ], changeDefaultColumns);
}

function* updateAnchoredMetrics(action) {
  let { newMetrics } = action;
  const { name } = action;
  let store = yield select(selector);
  const { features } = yield select(commonSelector);

  // We need to remove viewerSession metric which user has no permission flag
  if (!features[featureNames.VIEWER_DRILLDOWN_TIMELINE]) {
    newMetrics = newMetrics.filter(item => item !== 'ViewerSession');
  }

  if (!store[name]) {
    yield put({
      type: types.ADD_TABLE_DATA,
      code: name,
    });
    store = yield select(selector);
  }

  let { anchoredMetrics } = store[name];

  anchoredMetrics = Object.fromEntries(
    Object.keys(anchoredMetrics)
      .filter(v => newMetrics.includes(v) && anchoredMetrics[v])
      .map(m => [m, true]),
  );

  yield put({
    type: types.SET_VIEWER_SESSIONS_PARAMS,
    params: {
      anchoredMetrics,
      selectedMetrics: newMetrics,
    },
    name,
  });
}

function* watchUpdateAnchoredMetrics() {
  yield takeLatest([types.UPDATE_SELECTED_METRICS], updateAnchoredMetrics);
}

function* addTableData(action) {
  const { currentErrorCode } = action;
  const currentMetric = yield select(metricSelector);
  const timelineEnabled = yield select(featureSelector(featureNames.VIEWER_DRILLDOWN_TIMELINE));
  const customerAccountType = yield select(featureSelector(featureNames.CUSTOMER_ACCOUNT_TYPE));
  const defaultColumns = defaultMetricColumns(currentMetric, timelineEnabled, customerAccountType);
  const store = yield select(selector);
  if (!store[currentErrorCode]) {
    yield put({
      type: types.ADD_TABLE_DATA,
      code: currentErrorCode,
    });
    if (defaultColumns.length) {
      yield put({
        type: types.UPDATE_SELECTED_METRICS,
        newMetrics: defaultColumns,
        name: currentErrorCode,
      });
    }
  }
}

function* watchAddTableData() {
  yield takeLatest([SET_CURRENT_ERROR_CODE], addTableData);
}

function* addTableDataWithErrorInit(action) {
  const {
    state: { currentErrorCode },
  } = action;
  const store = yield select(selector);
  if (!store[currentErrorCode]) {
    yield put({
      type: types.ADD_TABLE_DATA,
      code: currentErrorCode,
    });
    const currentMetric = yield select(metricSelector);
    const timelineEnabled = yield select(featureSelector(featureNames.VIEWER_DRILLDOWN_TIMELINE));
    const customerAccountType = yield select(featureSelector(featureNames.CUSTOMER_ACCOUNT_TYPE));
    const defaultColumns = defaultMetricColumns(currentMetric, timelineEnabled,
      customerAccountType);
    const { payload } = yield select(errorDashboardSelector);
    const { isSharingPage } = yield select(commonSelector);
    if (!payload && !isSharingPage) {
      if (defaultColumns.length) {
        yield put({
          type: types.UPDATE_SELECTED_METRICS,
          newMetrics: defaultColumns,
          name: currentErrorCode,
        });
      }
    }
  }
}

function* watchAddTableDataWithErrorInit() {
  yield takeLatest([SET_BASE_STATE], addTableDataWithErrorInit);
}
export default [
  watchGetViewerSessionsData,
  watchSelectedMetricChange,
  watchUpdateAnchoredMetrics,
  watchAddTableData,
  watchAddTableDataWithErrorInit,
];
